import React, { useState, useEffect } from 'react';
import { FiPlus, FiEdit2, FiTrash2, FiKey, FiCalendar, FiCheck, FiX, FiBriefcase } from 'react-icons/fi';
import { toast } from 'react-hot-toast';
import { adminAPI } from '../../services/api';
import ConfirmDialog from '../../components/ConfirmDialog';

const AdminLicenses = () => {
  const [licenses, setLicenses] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingLicense, setEditingLicense] = useState(null);
  const [formData, setFormData] = useState({
    licenseSerial: '',
    licenseStart: '',
    licenseEnd: '',
    compID: '',
    deviceLimit: 10,
    isActive: true
  });
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, id: null });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [licensesData, companiesData] = await Promise.all([
        adminAPI.getLicenses(),
        adminAPI.getCompanies()
      ]);
      setLicenses(licensesData);
      setCompanies(companiesData);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateLicenseSerial = () => {
    const year = new Date().getFullYear();
    const month = String(new Date().getMonth() + 1).padStart(2, '0');
    const random = Math.random().toString(36).substring(2, 8).toUpperCase();
    return `ADV-${year}-${month}-${random}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        licenseSerial: formData.licenseSerial || generateLicenseSerial()
      };
      
      if (editingLicense) {
        await adminAPI.updateLicense(editingLicense.licenseID, payload);
      } else {
        await adminAPI.createLicense(payload);
      }
      // Refresh data
      await fetchData();
      resetForm();
    } catch (error) {
      console.error('Error saving license:', error);
      toast.error('Error saving license: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleDelete = (licenseId) => {
    setConfirmDialog({ isOpen: true, id: licenseId });
  };

  const confirmDelete = async () => {
    try {
      await adminAPI.deleteLicense(confirmDialog.id);
      await fetchData();
      setConfirmDialog({ isOpen: false, id: null });
      toast.success('License deleted successfully!');
    } catch (error) {
      console.error('Error deleting license:', error);
      toast.error('Error deleting license: ' + (error.response?.data?.error || error.message));
      setConfirmDialog({ isOpen: false, id: null });
    }
  };

  const handleToggleActive = async (licenseId, isActive) => {
    try {
      console.log('Toggle license active status:', licenseId, !isActive);
      // Update license status and refresh data
    } catch (error) {
      console.error('Error updating license status:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      licenseSerial: '',
      licenseStart: '',
      licenseEnd: '',
      compID: '',
      deviceLimit: 10,
      isActive: true
    });
    setEditingLicense(null);
    setShowCreateModal(false);
  };

  const startEdit = (license) => {
    setEditingLicense(license);
    setFormData({
      licenseSerial: license.licenseSerial,
      licenseStart: license.licenseStart,
      licenseEnd: license.licenseEnd,
      compID: license.compID.toString(),
      deviceLimit: license.deviceLimit || 10,
      isActive: license.isActive
    });
    setShowCreateModal(true);
  };

  const isLicenseExpired = (endDate) => {
    return new Date(endDate) < new Date();
  };

  const isLicenseExpiringSoon = (endDate) => {
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
    return new Date(endDate) <= thirtyDaysFromNow && new Date(endDate) >= new Date();
  };

  const getLicenseStatus = (license) => {
    if (isLicenseExpired(license.licenseEnd)) {
      return { status: 'expired', color: 'bg-red-100 text-red-800' };
    }
    if (isLicenseExpiringSoon(license.licenseEnd)) {
      return { status: 'expiring', color: 'bg-yellow-100 text-yellow-800' };
    }
    if (license.isActive) {
      return { status: 'active', color: 'bg-green-100 text-green-800' };
    }
    return { status: 'inactive', color: 'bg-gray-100 text-gray-800' };
  };

  const getCompanyName = (compID) => {
    const company = companies.find(c => c.compID === compID);
    return company ? company.compName : 'Unknown Company';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const activeLicenses = licenses.filter(l => l.isActive && !isLicenseExpired(l.licenseEnd));
  const expiredLicenses = licenses.filter(l => isLicenseExpired(l.licenseEnd));
  const expiringSoon = licenses.filter(l => isLicenseExpiringSoon(l.licenseEnd));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Admin Licenses</h1>
          <p className="mt-2 text-gray-600">
            Manage licenses across all companies
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="btn-primary flex items-center space-x-2"
        >
          <FiPlus className="h-4 w-4" />
          <span>Create License</span>
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="card">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg mr-3">
              <FiKey className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Total Licenses</p>
              <p className="text-2xl font-semibold text-gray-900">{licenses.length}</p>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg mr-3">
              <FiCheck className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Active</p>
              <p className="text-2xl font-semibold text-gray-900">{activeLicenses.length}</p>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 rounded-lg mr-3">
              <FiCalendar className="h-6 w-6 text-yellow-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Expiring Soon</p>
              <p className="text-2xl font-semibold text-gray-900">{expiringSoon.length}</p>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="flex items-center">
            <div className="p-2 bg-red-100 rounded-lg mr-3">
              <FiX className="h-6 w-6 text-red-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Expired</p>
              <p className="text-2xl font-semibold text-gray-900">{expiredLicenses.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Expiring Soon Alert */}
      {expiringSoon.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start">
            <FiCalendar className="h-5 w-5 text-yellow-600 mt-0.5 mr-3" />
            <div>
              <h3 className="text-sm font-medium text-yellow-800">
                {expiringSoon.length} license(s) expiring soon
              </h3>
              <p className="text-sm text-yellow-700 mt-1">
                The following licenses will expire within 30 days. Consider extending them.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Licenses List */}
      {licenses.length === 0 ? (
        <div className="card text-center py-12">
          <FiKey className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Licenses</h3>
          <p className="text-gray-600 mb-6">
            Create your first license to get started
          </p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn-primary"
          >
            Create License
          </button>
        </div>
      ) : (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    License
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Company
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Period
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Device Usage
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Assignment
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {licenses.map((license) => {
                  const statusInfo = getLicenseStatus(license);
                  return (
                    <tr key={license.licenseID} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="p-2 bg-blue-100 rounded-lg mr-3">
                            <FiKey className="h-5 w-5 text-blue-600" />
                          </div>
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {license.licenseSerial}
                            </div>
                            <div className="text-sm text-gray-500">ID: {license.licenseID}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <FiBriefcase className="h-4 w-4 text-gray-400 mr-2" />
                          <div>
                            <div className="text-sm text-gray-900">{license.compName}</div>
                            <div className="text-sm text-gray-500">ID: {license.compID}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {new Date(license.licenseStart).toLocaleDateString()}
                        </div>
                        <div className="text-sm text-gray-500">
                          to {new Date(license.licenseEnd).toLocaleDateString()}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          <span className="font-medium">{license.usedDevices || 0}</span>
                          <span className="text-gray-500"> / {license.deviceLimit || 'Unlimited'}</span>
                        </div>
                        <div className="text-xs text-gray-500">
                          {license.deviceLimit && license.usedDevices >= license.deviceLimit ? (
                            <span className="text-red-600">Limit reached</span>
                          ) : (
                            <span>{(license.deviceLimit || 0) - (license.usedDevices || 0)} available</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => handleToggleActive(license.licenseID, license.isActive)}
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusInfo.color} transition-colors cursor-pointer hover:opacity-75`}
                        >
                          {statusInfo.status}
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {license.assignedToAgent 
                          ? `Agent #${license.assignedToAgent}` 
                          : 'Unassigned'
                        }
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-2">
                          <button
                            onClick={() => startEdit(license)}
                            className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                          >
                            <FiEdit2 className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(license.licenseID)}
                            className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                          >
                            <FiTrash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-semibold mb-4">
              {editingLicense ? 'Edit License' : 'Create License'}
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="label">License Serial</label>
                <input
                  type="text"
                  value={formData.licenseSerial}
                  onChange={(e) => setFormData({...formData, licenseSerial: e.target.value})}
                  className="input"
                  placeholder="Leave empty to auto-generate"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Format: ADV-YYYY-MM-XXXXXX (auto-generated if empty)
                </p>
              </div>

              <div>
                <label className="label">Company *</label>
                <select
                  value={formData.compID}
                  onChange={(e) => setFormData({...formData, compID: e.target.value})}
                  className="input"
                  required
                >
                  <option value="">Select Company</option>
                  {companies.map(company => (
                    <option key={company.compID} value={company.compID}>
                      {company.compName}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Start Date *</label>
                  <input
                    type="date"
                    value={formData.licenseStart}
                    onChange={(e) => setFormData({...formData, licenseStart: e.target.value})}
                    className="input"
                    required
                  />
                </div>
                <div>
                  <label className="label">End Date *</label>
                  <input
                    type="date"
                    value={formData.licenseEnd}
                    onChange={(e) => setFormData({...formData, licenseEnd: e.target.value})}
                    className="input"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="label">Device Limit *</label>
                <input
                  type="number"
                  value={formData.deviceLimit}
                  onChange={(e) => setFormData({...formData, deviceLimit: parseInt(e.target.value) || 1})}
                  className="input"
                  min="1"
                  max="10000"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Maximum number of devices that can be activated with this license (1-10,000)
                </p>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({...formData, isActive: e.target.checked})}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="isActive" className="ml-2 text-sm text-gray-700">
                  Active license
                </label>
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={resetForm}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                >
                  {editingLicense ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ isOpen: false, id: null })}
        onConfirm={confirmDelete}
        title="Delete License"
        message="Are you sure you want to delete this license? This action cannot be undone."
      />
    </div>
  );
};

export default AdminLicenses;
